import { useMemo, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { Flex, Typography } from 'antd';
import { CaretRightIcon } from '@phosphor-icons/react';
import {
  colors,
  motion,
  radii,
  spacing,
  typography,
} from '../../../../theme-v2';
import { AddonLayout } from '../AddonLayout';
import { parseKeyPoints } from './parseKeyPoints';

const { Text } = Typography;

/**
 * Summary interaction callbacks, grouped into one prop. Optional so the
 * component runs standalone (Storybook, tests). The app wires these to
 * analytics.
 */
export interface SummaryAnalyticEvents {
  /**
   * A key point was expanded (closed → open only; collapsing is silent).
   * Only fires in accordion mode — plain-prose content has no items.
   */
  onItemExpand?: (itemIndex: number, totalItems: number) => void;
}

/** Visitor progress on the add-on, as the backend tracks it. */
export type SummaryStatus = 'Cold' | 'Warm' | 'Done';

export interface SummaryProps {
  /**
   * Key points body in markdown. Heading-structured content (`## Key point`)
   * renders as an expandable key-points accordion; unstructured content
   * falls back to plain prose. Supports paragraphs, lists, bold/italic,
   * inline code, links — rendered via react-markdown.
   */
  content: string;
  /** Loading state — shows shimmer placeholders instead of content. */
  loading?: boolean;
  /** Error message — shows error state instead of content. */
  error?: ReactNode;
  className?: string;
  /** Interaction callbacks (item expand). */
  analyticEvents?: SummaryAnalyticEvents;
  /**
   * Status restored from a previous visit. A restored `Done` (or `Warm`) is
   * not reported again; only a higher status is.
   */
  initialStatus?: SummaryStatus;
  /**
   * Called when the visitor opens the first key point (`Warm`) and when every
   * key point has been opened (`Done`). Never called with a lower status than
   * the one already reached. Accordion mode only.
   */
  onStatusChange?: (status: SummaryStatus) => void;
}

const STATUS_RANK: Record<SummaryStatus, number> = {
  Cold: 0,
  Warm: 1,
  Done: 2,
};

/**
 * Summary — renders the document's **Key points** add-on as an accordion of
 * expandable key points (falls back to plain prose when the markdown has no
 * headings). Owns its header text via i18n; apps just pass `content`.
 *
 * The component/prop names keep the `Summary` prefix because that is the
 * backend `AddOnType` this add-on maps to; everything user-facing says
 * "Key points".
 */
export const Summary = ({
  content,
  loading,
  error,
  className,
  analyticEvents,
  initialStatus = 'Cold',
  onStatusChange,
}: SummaryProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  // ASSUMPTION: the backend `ExecutiveSummary` is `{ content: string }` with no
  // structure, so we derive the key points from markdown headings client-side.
  // If the backend starts returning structured sections, drop parseKeyPoints
  // and consume that shape directly. See parseKeyPoints.ts.
  const keyPoints = useMemo(() => parseKeyPoints(content), [content]);
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());
  const reportedStatus = useRef<SummaryStatus>(initialStatus);
  // Key points opened at least once; collapsing does not remove them.
  const everOpenedIds = useRef<Set<string>>(new Set());

  // Toggles a key point; only the closed → open transition is an "expand"
  // for analytics — collapsing is silent.
  const toggle = (id: string, index: number) => {
    const next = new Set(openIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
      everOpenedIds.current.add(id);
      analyticEvents?.onItemExpand?.(index, keyPoints.length);
      const status: SummaryStatus =
        keyPoints.length > 0 &&
        keyPoints.every((point) => everOpenedIds.current.has(point.id))
          ? 'Done'
          : 'Warm';
      if (STATUS_RANK[status] > STATUS_RANK[reportedStatus.current]) {
        reportedStatus.current = status;
        onStatusChange?.(status);
      }
    }
    setOpenIds(next);
  };

  let body: ReactNode;
  if (loading) {
    body = (
      <Flex vertical gap={spacing.sm} aria-busy="true">
        <StyledShimmerLine $w="60%" />
        <StyledShimmerLine $w="95%" />
        <StyledShimmerLine $w="80%" />
        <StyledShimmerLine $w="90%" />
        <StyledShimmerLine $w="70%" />
      </Flex>
    );
  } else if (error) {
    body = (
      <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
        {error}
      </Text>
    );
  } else if (keyPoints.length > 0) {
    body = (
      <Flex vertical gap={spacing.sm}>
        {keyPoints.map((point, index) => {
          const open = openIds.has(point.id);
          return (
            <StyledAccordionItem key={point.id} $open={open}>
              <StyledAccordionHeader
                type="button"
                aria-expanded={open}
                onClick={() => toggle(point.id, index)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    strong
                    style={{
                      display: 'block',
                      fontSize: typography.size.md,
                      lineHeight: typography.lineHeight.medium,
                      color: colors.text.primary,
                    }}
                  >
                    {index + 1}. {point.title}
                  </Text>
                  {!open && point.teaser ? (
                    <Text
                      type="secondary"
                      style={{
                        display: 'block',
                        marginTop: spacing.xs,
                        fontSize: typography.size.sm,
                        lineHeight: typography.lineHeight.medium,
                      }}
                    >
                      {point.teaser}
                    </Text>
                  ) : null}
                </div>
                <CaretRightIcon
                  size={14}
                  color={colors.text.tertiary}
                  style={{
                    flexShrink: 0,
                    transform: open ? 'rotate(90deg)' : undefined,
                    transition: `transform ${motion.duration.base} ${motion.easing.standard}`,
                  }}
                />
              </StyledAccordionHeader>
              {open ? (
                <div
                  style={{ padding: `0 ${spacing.base}px ${spacing.base}px` }}
                >
                  <StyledMarkdown>
                    <ReactMarkdown>{point.body}</ReactMarkdown>
                  </StyledMarkdown>
                </div>
              ) : null}
            </StyledAccordionItem>
          );
        })}
      </Flex>
    );
  } else {
    body = (
      <StyledMarkdown>
        <ReactMarkdown>{content}</ReactMarkdown>
      </StyledMarkdown>
    );
  }

  // Header scrolls with the content (as in the mocks); hidden on error so the
  // message stands alone. The "N key concepts" subtitle is accordion-only.
  const isAccordion = !loading && !error && keyPoints.length > 0;

  return (
    <AddonLayout
      className={className}
      style={{
        background: colors.surface.base,
        borderRadius: radii.box,
        // Base typography — children (title, subtitle, teaser, error) inherit.
        fontFamily: typography.fontFamily,
        fontSize: typography.size.md,
      }}
    >
      <div style={{ padding: `${spacing.lg}px ${spacing.xl}px` }}>
        {!error ? (
          <div style={{ marginBottom: spacing.lg }}>
            <Text
              strong
              style={{
                display: 'block',
                fontSize: typography.size.lg,
                lineHeight: typography.lineHeight.tight,
                color: colors.text.primary,
              }}
            >
              {t('experienceV2.summary.title')}
            </Text>
            {isAccordion ? (
              <Text
                style={{
                  display: 'block',
                  marginTop: spacing.xs,
                  fontSize: typography.size.sm,
                  color: colors.text.tertiary,
                }}
              >
                {t('experienceV2.summary.keyConcepts', {
                  count: keyPoints.length,
                })}
              </Text>
            ) : null}
          </div>
        ) : null}
        {body}
      </div>
    </AddonLayout>
  );
};

// ─── Styled components ────────────────────────────────────────────────────

const StyledAccordionItem = styled.div<{ $open: boolean }>`
  border: 1px solid
    ${({ $open }) => ($open ? colors.accent.border : colors.border.subtle)};
  border-radius: ${radii.box}px;
  background: ${({ $open }) =>
    $open ? colors.accent.tint : colors.surface.base};
  transition:
    background ${motion.duration.base} ${motion.easing.standard},
    border-color ${motion.duration.base} ${motion.easing.standard};
`;

const StyledAccordionHeader = styled.button`
  appearance: none;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  font-family: ${typography.fontFamily};
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;
  padding: ${spacing.md}px ${spacing.base}px;
  cursor: pointer;
  user-select: none;
  border-radius: inherit;

  &:hover {
    background: ${colors.accent.tintSubtle};
  }
`;

const StyledMarkdown = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  line-height: ${typography.lineHeight.body};
  color: ${colors.text.secondary};

  /* Markdown element styling kept here so every KH key-points panel looks
     consistent across both admin and sales consumers. */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: ${typography.weight.bold};
    line-height: ${typography.lineHeight.tight};
    color: ${colors.text.primary};
    margin: ${spacing.base}px 0 ${spacing.sm}px;
  }
  h1 {
    font-size: ${typography.size.xl}px;
  }
  h2 {
    font-size: ${typography.size.lg}px;
  }
  h3 {
    font-size: ${typography.size.md}px;
  }
  p {
    margin: 0 0 ${spacing.sm}px;
    white-space: pre-line;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul,
  ol {
    margin: 0 0 ${spacing.sm}px;
    padding-left: ${spacing.lg}px;
  }
  li {
    margin-bottom: ${spacing.xs}px;
  }
  strong {
    font-weight: ${typography.weight.semibold};
    color: ${colors.text.primary};
  }
  a {
    color: ${colors.accent.base};
    text-decoration: underline;
  }
  code {
    background: ${colors.surface.muted};
    padding: 2px 4px;
    border-radius: ${radii.xs}px;
    font-size: 0.9em;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }
  blockquote {
    border-left: 3px solid ${colors.border.subtle};
    padding-left: ${spacing.md}px;
    margin: 0 0 ${spacing.sm}px;
    color: ${colors.text.secondary};
  }
`;

const StyledShimmerLine = styled.div<{ $w: string }>`
  height: 14px;
  width: ${({ $w }) => $w};
  border-radius: ${radii.xs}px;
  background: linear-gradient(
    90deg,
    ${colors.surface.muted} 0%,
    ${colors.border.subtle} 50%,
    ${colors.surface.muted} 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s ${motion.easing.standard} infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
